import { useRef, useState } from 'react';
import { useToast } from '@practium/ui';
import type { ManufacturerFormData } from '../types';
import { ManufacturerSchema } from '../schemas/manufacturer.schema';
import { usePostManufacturer, useUpdateManufacturer } from '@practium/features/api';

const maskCnpj = (value: string) => {
	const n = value.replace(/\D/g, '').slice(0, 14);
	if (n.length <= 2) return n;
	if (n.length <= 5) return `${n.slice(0, 2)}.${n.slice(2)}`;
	if (n.length <= 8) return `${n.slice(0, 2)}.${n.slice(2, 5)}.${n.slice(5)}`;
	if (n.length <= 12) return `${n.slice(0, 2)}.${n.slice(2, 5)}.${n.slice(5, 8)}/${n.slice(8)}`;
	return `${n.slice(0, 2)}.${n.slice(2, 5)}.${n.slice(5, 8)}/${n.slice(8, 12)}-${n.slice(12)}`;
};

const FIELD_LABELS: Record<string, string> = {
	tradingName: 'Nome Comercial',
	businessName: 'Razão Social',
	cnpj: 'CNPJ',
};

const DEFAULT_FORM: ManufacturerFormData = { tradingName: '', businessName: '', cnpj: '' };

export interface UseManufacturerFormOptions {
	initialData?: ManufacturerFormData;
	onSubmit: () => void;
	id?: string;
	onCreated?: (id: string) => void;
}

export const useManufacturerForm = ({ initialData, onSubmit, id, onCreated }: UseManufacturerFormOptions) => {
	const toast = useToast();
	const initialForm = initialData ? { ...DEFAULT_FORM, ...initialData } : { ...DEFAULT_FORM };
	const [form, setForm] = useState<ManufacturerFormData>(initialForm);
	const initialFormRef = useRef(initialForm);
	const [isLoading, setIsLoading] = useState(false);
	const [errors, setErrors] = useState<Record<string, string>>({});

	const { mutateAsync: createManufacturer } = usePostManufacturer();
	const { mutateAsync: updateManufacturer } = useUpdateManufacturer();

	const set = (key: keyof ManufacturerFormData, value: string) =>
		setForm(f => ({ ...f, [key]: value }));

	const isDirty = () => JSON.stringify(form) !== JSON.stringify(initialFormRef.current);

	const setCnpj = (raw: string) => set('cnpj', maskCnpj(raw));

	const handleSubmit = async () => {
		const result = ManufacturerSchema.safeParse(form);

		if (!result.success) {
			const fieldErrors: Record<string, string> = {};
			result.error.issues.forEach(issue => {
				const path = String(issue.path[0] ?? '');
				if (path) fieldErrors[path] = issue.message;
			});
			setErrors(fieldErrors);
			const uniquePaths = [...new Set(result.error.issues.map(i => String(i.path[0] ?? '')).filter(Boolean))];
			const labels = uniquePaths.map(p => FIELD_LABELS[p] ?? p).join(', ');
			toast.error(`Campos inválidos: ${labels}`);
			return;
		}

		setErrors({});
		setIsLoading(true);
		try {
			const payload = {
				tradingName: form.tradingName || undefined,
				businessName: form.businessName || undefined,
				cnpj: form.cnpj || undefined,
				isPublic: false,
			};
			if (id) {
				await updateManufacturer({ id, data: payload })
				toast.success('Fabricante atualizado com sucesso!')
			} else {
				const created = await createManufacturer(payload);
				onCreated?.(created?._id);
				toast.success('Fabricante salvo com sucesso!');
			}
			onSubmit();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Erro ao salvar fabricante');
		} finally {
			setIsLoading(false);
		}
	};

	return { form, errors, isLoading, set, setCnpj, handleSubmit, isDirty };
};
